import { useCallback, useEffect, useState } from "react";

/* ============================================================
   useTweaks — 3-tier precedence (locked, R2 verified)

   Priority (highest wins):
   1. window.__EDIT_MODE_DEFAULTS — read once at hydrate from
      EDITMODE block in index.html. Harness rewrites this on disk.
   2. localStorage[enssa.tweaks] — returning user, no harness.
   3. Hardcoded defaults — last resort.

   setTweak: state + localStorage + post __edit_mode_set_keys.
   reset: clear localStorage + post empty __edit_mode_set_keys.
   Open/close still toggled via __activate_edit_mode /
   __deactivate_edit_mode events (handled by TweaksPanel itself).

   Verified at legacy/tweaks-panel.jsx:221 — handler reads
   e.data.type only, no payload. No 4-tier branch needed.
   ============================================================ */

const STORAGE_KEY = "enssa.tweaks";

declare global {
  interface Window {
    __EDIT_MODE_DEFAULTS?: Record<string, unknown>;
  }
}

export function useTweaks<T extends Record<string, unknown>>(
  hardcodedDefaults: T,
): readonly [T, <K extends keyof T>(key: K, value: T[K]) => void, () => void] {
  const [tweaks, setTweaks] = useState<T>(() => hydrate(hardcodedDefaults));

  const setTweak = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      setTweaks((prev) => {
        const next = { ...prev, [key]: value };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        try {
          window.parent?.postMessage(
            { type: "__edit_mode_set_keys", edits: { [key]: value } },
            "*",
          );
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [],
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    try {
      window.parent?.postMessage(
        { type: "__edit_mode_set_keys", edits: {} },
        "*",
      );
    } catch {
      /* ignore */
    }
    setTweaks(() => hydrate(hardcodedDefaults));
  }, [hardcodedDefaults]);

  // Announce panel availability to harness (existing contract)
  useEffect(() => {
    try {
      window.parent?.postMessage({ type: "__edit_mode_available" }, "*");
    } catch {
      /* ignore */
    }
  }, []);

  return [tweaks, setTweak, reset] as const;
}

function hydrate<T extends Record<string, unknown>>(hardcoded: T): T {
  // Tier 1: EDITMODE block on window
  const editmode = window.__EDIT_MODE_DEFAULTS;
  if (editmode && typeof editmode === "object") {
    return { ...hardcoded, ...(editmode as Partial<T>) };
  }
  // Tier 2: localStorage
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as Partial<T>;
      return { ...hardcoded, ...parsed };
    }
  } catch {
    /* localStorage unavailable */
  }
  // Tier 3: hardcoded
  return hardcoded;
}
